import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronRight, 
  ShoppingCart, 
  Check, 
  Leaf, 
  Droplet, 
  ChevronDown,
  ChevronUp,
  Package,
  Award,
  Clock,
  Gift,
  X,
  ChevronLeft,
  ZoomIn,
  Truck,
  AlertCircle
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { getPriceInfo, isDiscountActive, getDiscountPercentage } from '../config/discountConfig';
import { getProductStock, STOCK_STATUS } from '../config/productConfig';
import RelatedProducts from '../components/RelatedProducts';
import friesImg from '../assets/images/fries.jpg';
import tastypixy from "../assets/Cutlery/tastypixy.jpg";
import tastypixy77 from "../assets/Cutlery/tastymaxi_777.jpg";

const FrenchFriesFork = () => {
  const [selectedQuantity, setSelectedQuantity] = useState('100');
  const [activeTab, setActiveTab] = useState('description');
  const [selectedImage, setSelectedImage] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isQuantityDropdownOpen, setIsQuantityDropdownOpen] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  
  const { addToCart } = useCart();
  
  // Get stock status from config
  const stockInfo = getProductStock('french-fries-fork');

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isLightboxOpen) return;
      
      if (e.key === 'Escape') {
        setIsLightboxOpen(false);
      } else if (e.key === 'ArrowRight') {
        setLightboxImageIndex((prev) => (prev + 1) % productImages.length);
      } else if (e.key === 'ArrowLeft') {
        setLightboxImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen]);

  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isLightboxOpen]);

  const openLightbox = (index) => {
    setLightboxImageIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const goToNextImage = () => {
    setLightboxImageIndex((prev) => (prev + 1) % productImages.length);
  };

  const goToPrevImage = () => {
    setLightboxImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const pricingOptions = [
    { units: 'sample', price: 0, label: 'Sample', description: 'Free sample' },
    { units: 30, price: 2.5, label: '30 forks', description: '1 pack' },
  ];

  const getSelectedPricing = () => {
    return pricingOptions.find(opt => String(opt.units) === selectedQuantity) || pricingOptions[1];
  };

  const selectedPricing = getSelectedPricing();

  const productImages = [
    { id: 0, src: friesImg, alt: "French Fries Fork - Main Product View" },
    { id: 1, src: tastypixy, alt: "French Fries Fork - Detail View" },
    { id: 2, src: tastypixy77, alt: "French Fries Fork - In Use" }
  ];

  const product = {
    name: "French Fries Fork",
    subtitle: "Ketchup-Flavored Edible Chip Fork",
    sku: "CUTLERY-FRIESFORK-001",
    unitsPerPack: 30
  };

  const keyFeatures = [
    "Unique ketchup flavor",
    "Perfect for fries, currywurst & finger food",
    "Crispy and eco-friendly",
    "Plastic-free snack utensil",
    "100% Edible & Biodegradable EATlery® by FRENVI made in Germany"
  ];

  const productDetails = {
    dimensions: {
      length: "8.5 cm",
      width: "1.5 cm",
      weight: "3,4g per fork",
      style: "Two-prong mini fork"
    },
ingredients: [
  "Wheat flour, Sorghum flour, Brown sugar, Refined wheat flour",
  "Onion extract, Rice bran oil, Iodized salt, Wheat protein",
  "Tomato extract, Garlic extract, Rice flour, Soy lecithin",
  "Corn flour, Citric acid, Paprika extract, Cinnamon powder",
  "Potassium carbonate, Added antioxidants, Natural flavors (tomato ketchup)"
],
nutritionalInfo: {
  calories: "13.8 kcal",
  fat: "0.18 g",
  monounsaturatedFat: "0.07 g",
  carbohydrates: "2.66 g",
  sugars: "0.44 g",
  fiber: "0.33 g",
  protein: "0.38 g",
  salt: "0.0326 g"
},
    storage: "Store in a cool, dry place (15-25°C) away from direct sunlight. Keep in original packaging until use. Best consumed within 18 months of production date.",
    certifications: ["ISO 22000", "FSSAI Certified", "FDA Approved", "Vegan Friendly"],
    shelfLife: "18 months from production date"
  };

  const faqs = [
    {
      question: "What makes this fork special for fries?",
      answer: "Our French Fries Fork features a unique ketchup flavor that perfectly complements fries and other finger foods. The two-prong design is specifically made to pick up fries easily, and the compact size fits perfectly in snack containers."
    },
    {
      question: "Can I use these for foods other than fries?",
      answer: "Absolutely! While designed with fries in mind, these forks are perfect for currywurst, chicken nuggets, mozzarella sticks, and any finger food. The ketchup flavor adds an extra dimension to your snacking experience."
    },
    {
      question: "How strong is the ketchup flavor?",
      answer: "The ketchup flavor is pleasant and complementary, not overpowering. It enhances your snack rather than masking it. Think of it as having ketchup built right into your utensil!"
    },
    {
      question: "Are these suitable for food trucks and fast food outlets?",
      answer: "Yes! These forks are ideal for food trucks, fast food restaurants, stadiums, and anywhere fries are served. They're a great conversation starter and eco-friendly alternative to plastic forks."
    }
  ];

  return (
    <div className="edible-cup-page">
      <nav className="breadcrumb-nav">
        <div className="container">
          <ol className="breadcrumb">
            <li><Link to="/">Home</Link></li>
            <ChevronRight size={14} />
            <li><Link to="/cutlery-tableware">Cutlery & Tableware</Link></li>
            <ChevronRight size={14} />
            <li className="current">French Fries Fork</li>
          </ol>
        </div>
      </nav>

      <section className="product-main-section">
        <div className="container">
          <div className="product-layout">
            <div className="product-gallery">
              <div className="main-image-container" onClick={() => openLightbox(selectedImage)}>
                <span className="zoom-hint">
                  <ZoomIn size={20} />
                  Click to zoom
                </span>
                <img 
                  src={productImages[selectedImage].src} 
                  alt={productImages[selectedImage].alt}
                  className="main-product-image clickable"
                />
              </div>
              <div className="thumbnail-gallery">
                {productImages.map((img, index) => (
                  <button
                    key={img.id}
                    className={`thumbnail-btn ${selectedImage === index ? 'active' : ''}`}
                    onClick={() => setSelectedImage(index)}
                    onDoubleClick={() => openLightbox(index)}
                  >
                    <img src={img.src} alt={img.alt} />
                  </button>
                ))}
              </div>
              
              {/* Key Features - Under Image */}
              <div className="key-features">
                <h3>Key Features</h3>
                <ul>
                  {keyFeatures.map((feature, index) => (
                    <li key={index}>
                      <Leaf size={16} className="feature-icon" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="product-info">
              <span className="product-sku">SKU: {product.sku}</span>
              <h1 className="product-title">{product.name}</h1>
              <p className="product-subtitle">{product.subtitle}</p>

              <div className="carton-pricing">
                {selectedPricing.units === 'sample' ? (
                  <div className="price-main">
                    <span className="price free">FREE</span>
                    <span className="price-label">Sample</span>
                  </div>
                ) : (
                  <>
                    {(() => {
                      const priceInfo = getPriceInfo(selectedPricing.price);
                      return (
                        <div className={`price-main ${priceInfo.hasDiscount ? 'has-discount' : ''}`}>
                          {priceInfo.hasDiscount && (
                            <>
                              <span className="discount-badge">-{priceInfo.discountPercentage}</span>
                              <span className="price-original">€{priceInfo.originalPrice.toFixed(2)}</span>
                            </>
                          )}
                          <span className={`price ${priceInfo.hasDiscount ? 'discounted' : ''}`}>
                            €{priceInfo.discountedPrice.toFixed(2)}
                          </span>
                          <span className="price-label">for {selectedPricing.units} forks</span>
                        </div>
                      );
                    })()}
                  </>
                )}
                <div className={`stock-status ${stockInfo.isSoldOut ? 'sold-out' : 'in-stock'}`}>
                  {stockInfo.isSoldOut ? (
                    <>
                      <AlertCircle size={20} />
                      {stockInfo.statusLabel}
                    </>
                  ) : (
                    <>
                      <Check size={20} />
                      {stockInfo.statusLabel}
                    </>
                  )}
                </div>
                {stockInfo.isInStock && (
                  <div className="delivery-time">
                    <Truck size={16} />
                    <span>Delivery: {stockInfo.deliveryTime}</span>
                  </div>
                )}
              </div>

              <div className="carton-info-box">
                <Package size={20} />
                <div>
                  <strong>Sold by Pack</strong>
                  <span>Each pack contains {product.unitsPerPack} forks</span>
                </div>
              </div>

              <div className="size-info-box">
                <Leaf size={20} />
                <div>
                  <strong>Ketchup Flavored</strong>
                  <span>The perfect companion for your fries</span>
                </div>
              </div>

              <div className="purchase-controls-carton">
                <div className="quantity-dropdown-container">
                  <label>Select Quantity:</label>
                  <div className="quantity-dropdown">
                    <button 
                      className="quantity-dropdown-trigger"
                      onClick={() => setIsQuantityDropdownOpen(!isQuantityDropdownOpen)}
                    >
                      <span>
                        {selectedPricing.units === 'sample' ? (
                          <>
                            <Gift size={16} />
                            Sample - FREE
                          </>
                        ) : (
                          <>
                            {(() => {
                              const priceInfo = getPriceInfo(selectedPricing.price);
                              return (
                                <>
                                  {selectedPricing.units} forks - 
                                  {priceInfo.hasDiscount && (
                                    <span className="dropdown-original-price">€{priceInfo.originalPrice.toFixed(2)}</span>
                                  )}
                                  <span className={priceInfo.hasDiscount ? 'dropdown-discounted-price' : ''}>
                                    €{priceInfo.discountedPrice.toFixed(2)}
                                  </span>
                                </>
                              );
                            })()}
                          </>
                        )}
                      </span>
                      <ChevronDown size={18} className={isQuantityDropdownOpen ? 'rotate' : ''} />
                    </button>
                    
                    {isQuantityDropdownOpen && (
                      <div className="quantity-dropdown-menu">
                        {pricingOptions.map((option) => (
                          <button
                            key={option.units}
                            className={`quantity-option ${String(option.units) === selectedQuantity ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedQuantity(String(option.units));
                              setIsQuantityDropdownOpen(false);
                            }}
                          >
                            <div className="option-main">
                              {option.units === 'sample' ? (
                                <>
                                  <Gift size={16} />
                                  <span className="option-cups">Sample</span>
                                </>
                              ) : (
                                <>
                                  <span className="option-cups">{option.units} forks</span>
                                  <span className="option-cartons">({option.description})</span>
                                </>
                              )}
                            </div>
                            <div className="option-price">
                              {option.units === 'sample' ? (
                                <span className="free-tag">FREE</span>
                              ) : (
                                <>
                                  {(() => {
                                    const optPriceInfo = getPriceInfo(option.price);
                                    return (
                                      <>
                                        {optPriceInfo.hasDiscount && (
                                          <span className="option-original-price">€{optPriceInfo.originalPrice.toFixed(2)}</span>
                                        )}
                                        <span className={optPriceInfo.hasDiscount ? 'option-discounted-price' : ''}>
                                          €{optPriceInfo.discountedPrice.toFixed(2)}
                                        </span>
                                      </>
                                    );
                                  })()}
                                </>
                              )}
                            </div>
                            {String(option.units) === selectedQuantity && <Check size={16} className="check-icon" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button 
                  className={`add-to-cart-btn ${isAddedToCart ? 'added' : ''} ${stockInfo.isSoldOut ? 'disabled sold-out-btn' : ''}`}
                  disabled={stockInfo.isSoldOut}
                  onClick={() => {
                    if (stockInfo.isSoldOut) return;
                    
                    if (selectedPricing.units === 'sample') {
                      window.location.href = '/contact';
                      return;
                    }
                    
                    const cartItem = {
                      id: 'french-fries-fork',
                      name: 'French Fries Fork',
                      variant: `${selectedPricing.units} forks`,
                      price: selectedPricing.price,
                      units: selectedPricing.units,
                      quantity: 1,
                      image: productImages[0].src
                    };
                    
                    addToCart(cartItem);
                    setIsAddedToCart(true);
                    setTimeout(() => setIsAddedToCart(false), 2000);
                  }}
                >
                  {stockInfo.isSoldOut ? (
                    <>
                      <AlertCircle size={20} />
                      Sold Out
                    </>
                  ) : isAddedToCart ? (
                    <>
                      <Check size={20} />
                      Added to Cart!
                    </>
                  ) : (
                    <>
                      <ShoppingCart size={20} />
                      {selectedPricing.units === 'sample' ? (
                        'Request Free Sample'
                      ) : (
                        <>
                          {(() => {
                            const btnPriceInfo = getPriceInfo(selectedPricing.price);
                            return (
                              <>
                                Add to Cart - 
                                {btnPriceInfo.hasDiscount && (
                                  <span className="btn-original-price">€{btnPriceInfo.originalPrice.toFixed(2)}</span>
                                )}
                                <span className={btnPriceInfo.hasDiscount ? 'btn-discounted-price' : ''}>
                                  €{btnPriceInfo.discountedPrice.toFixed(2)}
                                </span>
                              </>
                            );
                          })()}
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="product-tabs-section">
        <div className="container">
          <div className="tabs-navigation">
            <button 
              className={`tab-btn ${activeTab === 'description' ? 'active' : ''}`}
              onClick={() => setActiveTab('description')}
            >
              Description
            </button>
            <button 
              className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Product Details
            </button>
            <button 
              className={`tab-btn ${activeTab === 'faq' ? 'active' : ''}`}
              onClick={() => setActiveTab('faq')}
            >
              FAQ
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'description' && (
              <div className="tab-panel description-panel">
                <h2>French Fries Fork - Ketchup Meets Convenience</h2>
                <p>
                  The fork combines unique ketchup flavor, practical use, and sustainability in an edible chip fork. Perfect for fries, currywurst, or finger food, plastic-free, crispy, and eco-friendly.
                </p>
                <p>
                  This innovative snack utensil brings the flavor of ketchup right to your fingertips—literally! The two-prong design is perfect for picking up fries, while the compact size fits easily in any snack container or cup.
                </p>
                <p>
                  Ideal for food trucks, fast food outlets, stadiums, festivals, and anyone who loves fries with a sustainable twist.
                </p>
                
                <div className="description-features">
                  <div className="desc-feature">
                    <Package size={32} />
                    <h4>Compact Two-Prong Design</h4>
                    <p>8.5cm length perfect for fries. Fits in any snack container.</p>
                  </div>
                  <div className="desc-feature">
                    <Droplet size={32} />
                    <h4>Ketchup Infused</h4>
                    <p>Real ketchup flavor built in. The perfect fry companion.</p>
                  </div>
                  <div className="desc-feature">
                    <Leaf size={32} />
                    <h4>Snack & Eat</h4>
                    <p>Use it for your fries, then eat the fork. Zero waste snacking.</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'details' && (
              <div className="tab-panel details-panel">
                <div className="details-grid">
                  <div className="detail-section">
                    <h3><Package size={20} /> Dimensions</h3>
                    <table className="details-table">
                      <tbody>
                        <tr><td>Length</td><td>{productDetails.dimensions.length}</td></tr>
                        <tr><td>Width</td><td>{productDetails.dimensions.width}</td></tr>
                        <tr><td>Weight</td><td>{productDetails.dimensions.weight}</td></tr>
                        <tr><td>Style</td><td>{productDetails.dimensions.style}</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="detail-section">
                    <h3><Leaf size={20} /> Ingredients</h3>
                    <ul className="ingredients-list">
                      {productDetails.ingredients.map((ingredient, index) => (
                        <li key={index}>{ingredient}</li>
                      ))}
                    </ul>
                    <p className="allergen-warning">
                      <strong>Allergen Information:</strong> Contains wheat and gluten.
                    </p>
                  </div>

                  <div className="detail-section">
                    <h3><Award size={20} /> Nutritional Information (per fork)</h3>
                    <table className="details-table">
                      <tbody>
                        <tr><td>Energy</td><td>{productDetails.nutritionalInfo.calories}</td></tr>
                        <tr><td>Protein</td><td>{productDetails.nutritionalInfo.protein}</td></tr>
                        <tr><td>Carbohydrates</td><td>{productDetails.nutritionalInfo.carbohydrates}</td></tr>
                        <tr><td>Fat</td><td>{productDetails.nutritionalInfo.fat}</td></tr>
                        <tr><td>Fiber</td><td>{productDetails.nutritionalInfo.fiber}</td></tr>
                        <tr><td>Salt</td><td>{productDetails.nutritionalInfo.salt}</td></tr>
                        <tr><td>Sugars</td><td>{productDetails.nutritionalInfo.sugars}</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="detail-section">
                    <h3><Clock size={20} /> Storage & Shelf Life</h3>
                    <p>{productDetails.storage}</p>
                    <p><strong>Shelf Life:</strong> {productDetails.shelfLife}</p>
                  </div>

                  <div className="detail-section full-width">
                    <h3><Award size={20} /> Certifications</h3>
                    <div className="certifications-list">
                      {productDetails.certifications.map((cert, index) => (
                        <span key={index} className="cert-badge">{cert}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'faq' && (
              <div className="tab-panel faq-panel">
                <h2>Frequently Asked Questions - French Fries Fork</h2>
                <div className="faq-list">
                  {faqs.map((faq, index) => (
                    <div 
                      key={index} 
                      className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
                    >
                      <button 
                        className="faq-question"
                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      >
                        <span>{faq.question}</span>
                        {expandedFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                      {expandedFaq === index && (
                        <div className="faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related Products Section */}
      <RelatedProducts currentProductId="fries-fork" category="cutlery-tableware" />

      {isLightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <X size={28} />
            </button>
            
            <button className="lightbox-nav lightbox-prev" onClick={goToPrevImage}>
              <ChevronLeft size={36} />
            </button>
            
            <div className="lightbox-image-container">
              <img 
                src={productImages[lightboxImageIndex].src} 
                alt={productImages[lightboxImageIndex].alt}
                className="lightbox-image"
              />
            </div>
            
            <button className="lightbox-nav lightbox-next" onClick={goToNextImage}>
              <ChevronRight size={36} />
            </button>
            
            <div className="lightbox-thumbnails">
              {productImages.map((img, index) => (
                <button
                  key={img.id}
                  className={`lightbox-thumb ${lightboxImageIndex === index ? 'active' : ''}`}
                  onClick={() => setLightboxImageIndex(index)}
                >
                  <img src={img.src} alt={img.alt} />
                </button>
              ))}
            </div>
            
            <div className="lightbox-counter">
              {lightboxImageIndex + 1} / {productImages.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FrenchFriesFork;
